import React from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const CGV = () => {
  return (
    <div className="font-sans min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-black to-flowkraft-primary text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-8">
            <FileText className="w-16 h-16 text-flowkraft-secondary" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-center mb-6">
            Conditions Générales de Vente
          </h1>
          <p className="text-xl text-center text-gray-200 max-w-3xl mx-auto">
            Nos conditions de vente et de prestation de services
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">

          {/* Préambule */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Préambule</h2>
            <p className="text-gray-700 mb-4">
              Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre
              FlowKraft Agency, micro-entreprise située à Toulouse, France (ci-après "le Prestataire") et
              toute personne physique ou morale souhaitant bénéficier des services proposés (ci-après "le Client").
            </p>
            <p className="text-gray-700">
              Toute commande implique l'acceptation sans réserve des présentes CGV qui prévalent sur tout
              autre document du Client, notamment sur toutes conditions générales d'achat.
            </p>
          </div>

          {/* Article 1 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Objet</h2>
            <p className="text-gray-700 mb-4">
              Les présentes CGV ont pour objet de définir les droits et obligations des parties dans le cadre
              de la vente de services de développement web, développement mobile, design UX/UI, marketing digital
              et consulting proposés par le Prestataire.
            </p>
            <p className="text-gray-700">
              Les services proposés sont ceux qui figurent sur le site internet{" "}
              <a href="https://flowkraftagency.com" className="text-flowkraft-primary hover:underline">
                https://flowkraftagency.com
              </a>
              , ainsi que toute prestation sur mesure définie dans un devis personnalisé.
            </p>
          </div>

          {/* Article 2 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">2. Devis et commande</h2>
            <p className="text-gray-700 mb-4">
              <strong>2.1 Demande de devis</strong>
            </p>
            <p className="text-gray-700 mb-4">
              Le Client peut demander un devis gratuit et sans engagement via le formulaire de contact du site
              ou par email. Le devis est valable pendant 30 jours à compter de sa date d'émission.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>2.2 Validation de la commande</strong>
            </p>
            <p className="text-gray-700">
              La commande n'est définitive qu'après signature du devis par le Client et réception de l'acompte
              le cas échéant. Le Client reconnaît avoir pris connaissance des présentes CGV et les accepte
              sans réserve.
            </p>
          </div>

          {/* Article 3 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">3. Prix</h2>
            <p className="text-gray-700 mb-4">
              <strong>3.1 Tarification</strong>
            </p>
            <p className="text-gray-700 mb-4">
              Les prix sont indiqués en euros hors taxes (HT). En tant que micro-entrepreneur, le Prestataire
              bénéficie de la franchise en base de TVA et ne facture donc pas de TVA.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>3.2 Révision des prix</strong>
            </p>
            <p className="text-gray-700">
              Les prix peuvent être révisés à tout moment. Toutefois, les prix applicables sont ceux en vigueur
              au jour de la commande. Toute modification du cahier des charges initial peut donner lieu à un
              nouveau devis.
            </p>
          </div>

          {/* Article 4 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">4. Conditions de paiement</h2>
            <p className="text-gray-700 mb-4">
              <strong>4.1 Modalités de paiement</strong>
            </p>
            <p className="text-gray-700 mb-4">
              Le paiement s'effectue selon les modalités suivantes :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
              <li>Acompte de 30% à 50% à la signature du devis</li>
              <li>Paiements intermédiaires selon l'avancement du projet (si prévu au devis)</li>
              <li>Solde à la livraison finale ou à la mise en production</li>
            </ul>
            <p className="text-gray-700 mb-4">
              <strong>4.2 Moyens de paiement acceptés</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
              <li>Virement bancaire</li>
              <li>Carte bancaire (via plateforme sécurisée)</li>
              <li>PayPal</li>
            </ul>
            <p className="text-gray-700 mb-4">
              <strong>4.3 Délai de paiement</strong>
            </p>
            <p className="text-gray-700 mb-4">
              Le règlement doit intervenir dans un délai maximum de 30 jours à compter de la date d'émission
              de la facture.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>4.4 Retard de paiement</strong>
            </p>
            <p className="text-gray-700">
              En cas de retard de paiement, des pénalités de retard seront appliquées au taux de 3 fois le
              taux d'intérêt légal, ainsi qu'une indemnité forfaitaire de 40€ pour frais de recouvrement.
            </p>
          </div>

          {/* Article 5 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5. Délais de réalisation</h2>
            <p className="text-gray-700 mb-4">
              Les délais de réalisation sont indiqués à titre indicatif dans le devis. Ils courent à compter
              de la réception de l'acompte et de tous les éléments nécessaires à la réalisation du projet
              (contenus, visuels, accès, etc.).
            </p>
            <p className="text-gray-700">
              Le Prestataire s'engage à respecter les délais convenus. Toutefois, un retard ne saurait donner
              lieu à des pénalités ou à l'annulation de la commande, sauf en cas de retard manifestement
              excessif.
            </p>
          </div>

          {/* Article 6 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Obligations du Client</h2>
            <p className="text-gray-700 mb-4">
              Le Client s'engage à :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Fournir tous les éléments nécessaires à la réalisation du projet dans les délais convenus</li>
              <li>Désigner un interlocuteur unique pour le suivi du projet</li>
              <li>Répondre aux demandes de validation dans un délai raisonnable (7 jours maximum)</li>
              <li>Fournir des contenus conformes à la législation en vigueur</li>
              <li>Respecter les droits de propriété intellectuelle de tiers</li>
              <li>Effectuer les paiements selon l'échéancier convenu</li>
            </ul>
          </div>

          {/* Article 7 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Obligations du Prestataire</h2>
            <p className="text-gray-700 mb-4">
              Le Prestataire s'engage à :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Mettre en œuvre tous les moyens nécessaires à la bonne réalisation du projet</li>
              <li>Respecter les délais convenus dans la mesure du possible</li>
              <li>Tenir le Client informé de l'avancement du projet</li>
              <li>Garantir la confidentialité des informations communiquées</li>
              <li>Livrer un travail conforme aux spécifications du devis</li>
              <li>Former le Client à l'utilisation des outils développés si prévu au contrat</li>
            </ul>
          </div>

          {/* Article 8 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Propriété intellectuelle</h2>
            <p className="text-gray-700 mb-4">
              <strong>8.1 Cession des droits</strong>
            </p>
            <p className="text-gray-700 mb-4">
              La cession des droits d'exploitation sur les créations réalisées par le Prestataire n'est
              effective qu'après complet paiement du prix convenu. Cette cession porte sur les droits
              d'exploitation suivants : reproduction, représentation, adaptation.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>8.2 Droit de signature et de référence</strong>
            </p>
            <p className="text-gray-700 mb-4">
              Le Prestataire se réserve le droit de mentionner les réalisations effectuées pour le Client
              dans ses supports de communication (portfolio, site web, réseaux sociaux), sauf opposition
              écrite du Client.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>8.3 Licences et outils tiers</strong>
            </p>
            <p className="text-gray-700">
              Le Client reste propriétaire des contenus qu'il fournit. Les éventuelles licences de logiciels
              ou services tiers nécessaires au fonctionnement du projet sont à la charge du Client.
            </p>
          </div>

          {/* Article 9 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Garantie et responsabilité</h2>
            <p className="text-gray-700 mb-4">
              <strong>9.1 Garantie de conformité</strong>
            </p>
            <p className="text-gray-700 mb-4">
              Le Prestataire garantit la conformité des prestations réalisées aux spécifications du devis.
              Toute non-conformité devra être signalée dans un délai de 15 jours suivant la livraison.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>9.2 Limitation de responsabilité</strong>
            </p>
            <p className="text-gray-700 mb-4">
              La responsabilité du Prestataire est limitée au montant de la prestation. Le Prestataire ne
              pourra être tenu responsable des dommages indirects (perte de chiffre d'affaires, perte de
              données, etc.).
            </p>
            <p className="text-gray-700">
              Le Prestataire ne saurait être tenu pour responsable des contenus fournis par le Client et
              de leur conformité à la législation en vigueur.
            </p>
          </div>

          {/* Article 10 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Maintenance et support</h2>
            <p className="text-gray-700 mb-4">
              La maintenance corrective (correction de bugs) est assurée gratuitement pendant une période
              de 30 jours suivant la livraison finale.
            </p>
            <p className="text-gray-700">
              Au-delà de cette période, ou pour toute maintenance évolutive (nouvelles fonctionnalités),
              un contrat de maintenance spécifique pourra être établi sur devis.
            </p>
          </div>

          {/* Article 11 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">11. Confidentialité</h2>
            <p className="text-gray-700">
              Les parties s'engagent à respecter la confidentialité des informations échangées dans le cadre
              de leur collaboration. Cette obligation de confidentialité reste valable pendant toute la durée
              de la relation contractuelle et pendant 2 ans après son terme.
            </p>
          </div>

          {/* Article 12 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">12. Résiliation</h2>
            <p className="text-gray-700 mb-4">
              <strong>12.1 Résiliation par le Client</strong>
            </p>
            <p className="text-gray-700 mb-4">
              Le Client peut résilier le contrat à tout moment. Les sommes déjà versées resteront acquises
              au Prestataire au prorata du travail effectué. Les livrables en cours seront facturés au
              prorata de leur avancement.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>12.2 Résiliation par le Prestataire</strong>
            </p>
            <p className="text-gray-700">
              En cas de manquement grave du Client à ses obligations (notamment défaut de paiement,
              non-fourniture des éléments nécessaires), le Prestataire pourra résilier le contrat de plein
              droit après mise en demeure restée sans effet pendant 15 jours.
            </p>
          </div>

          {/* Article 13 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">13. Force majeure</h2>
            <p className="text-gray-700">
              Les parties ne pourront être tenues pour responsables si la non-exécution ou le retard dans
              l'exécution de l'une quelconque de leurs obligations découle d'un cas de force majeure, au
              sens de l'article 1218 du Code civil.
            </p>
          </div>

          {/* Article 14 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">14. Protection des données personnelles</h2>
            <p className="text-gray-700">
              Les données personnelles collectées dans le cadre de la relation commerciale sont traitées
              conformément à notre{" "}
              <Link to="/politique-confidentialite" className="text-flowkraft-primary hover:underline">
                Politique de confidentialité
              </Link>
              {" "}et au RGPD.
            </p>
          </div>

          {/* Article 15 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">15. Modifications des CGV</h2>
            <p className="text-gray-700">
              Le Prestataire se réserve le droit de modifier les présentes CGV à tout moment. Les CGV
              applicables sont celles en vigueur à la date de la commande.
            </p>
          </div>

          {/* Article 16 */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">16. Droit applicable et règlement des litiges</h2>
            <p className="text-gray-700 mb-4">
              Les présentes CGV sont soumises au droit français.
            </p>
            <p className="text-gray-700 mb-4">
              En cas de litige, les parties s'engagent à rechercher une solution amiable avant toute action
              judiciaire. À défaut d'accord amiable, le litige sera porté devant les tribunaux compétents
              du ressort de Toulouse.
            </p>
            <p className="text-gray-700">
              Conformément à l'article L.612-1 du Code de la consommation, le Client consommateur a le droit
              de recourir gratuitement à un médiateur de la consommation en vue de la résolution amiable du
              litige qui l'oppose au Prestataire.
            </p>
          </div>

          {/* Contact */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact</h2>
            <p className="text-gray-700 mb-4">
              Pour toute question concernant ces CGV :
            </p>
            <div className="text-gray-700 space-y-2">
              <p><strong>FlowKraft Agency</strong></p>
              <p><strong>Email :</strong> <a href="mailto:info@flowkraftagency.com" className="text-flowkraft-primary hover:underline">info@flowkraftagency.com</a></p>
              <p><strong>Téléphone :</strong> <a href="tel:+33652794562" className="text-flowkraft-primary hover:underline">+33 (0)6 52 79 45 62</a></p>
              <p><strong>Adresse :</strong> Toulouse, France</p>
            </div>
          </div>

          {/* Date de mise à jour */}
          <div className="text-gray-600 text-sm italic">
            <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/main/2149755776.jpg)" }}
        ></div>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-flowkraft-primary to-black opacity-90"></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Demandez votre devis gratuit et sans engagement
          </p>
          <Link
            to="/#contact"
            className="inline-block bg-white text-flowkraft-primary px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Demander un devis
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CGV;
